/**
 * TranscriptStream — renders the conversation turns in a chat-bubble layout.
 *
 * - User turns: right-aligned stone bubbles
 * - AI turns: left-aligned amber/warm bubbles
 * - partialTranscript: italic gray bubble at bottom (voice mode in-progress)
 * - streamingAiText: streaming AI response bubble (animating)
 *
 * Security (T-02-53): All content rendered as React text nodes (auto-escaped).
 * NEVER uses dangerouslySetInnerHTML.
 *
 * Auto-scrolls to bottom when turns or streaming text change.
 */

import { useEffect, useRef } from 'react';
import type { Turn } from '../../store/sessionStore';

interface TranscriptStreamProps {
  turns: Turn[];
  streamingAiText: string;
  partialTranscript: string;
}

export function TranscriptStream({ turns, streamingAiText, partialTranscript }: TranscriptStreamProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns.length, streamingAiText.length, partialTranscript.length]);

  return (
    <div
      className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
      aria-label="Conversation transcript"
      aria-live="polite"
      aria-atomic="false"
    >
      {turns.length === 0 && !streamingAiText && !partialTranscript && (
        <div className="flex items-center justify-center h-full">
          <p className="text-stone-400 text-sm">Connecting to your interview session…</p>
        </div>
      )}

      {/* Committed turns */}
      {turns.map((turn) => (
        <div
          key={turn.id}
          className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={[
              'max-w-[80%] rounded-2xl text-sm leading-relaxed overflow-hidden',
              turn.role === 'user'
                ? 'bg-stone-800 text-white rounded-tr-sm'
                : 'bg-amber-50 text-stone-800 border border-amber-200 rounded-tl-sm',
            ].join(' ')}
            aria-label={`${turn.role === 'user' ? 'You' : 'AI'}: ${turn.content}`}
          >
            {/* In-chat image previews (user turns only) */}
            {turn.mediaPreviews && turn.mediaPreviews.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2">
                {turn.mediaPreviews.map((url, i) => (
                  <img key={i} src={url} alt={`Attached photo ${i + 1}`} className="h-40 max-w-full rounded-lg object-cover" />
                ))}
              </div>
            )}
            {/* Document chip (user turns only) */}
            {turn.documentFilename && (
              <div className="px-3 pt-2 pb-1 flex items-center gap-1.5 text-xs text-stone-300">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 flex-none" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" clipRule="evenodd" />
                </svg>
                <span className="truncate max-w-[160px]">{turn.documentFilename}</span>
              </div>
            )}
            {/* T-02-53: plain text node — no dangerouslySetInnerHTML */}
            {turn.content && (
              <p className="px-4 py-3">{turn.content}</p>
            )}
          </div>
        </div>
      ))}

      {/* Streaming AI response (live) */}
      {streamingAiText && (
        <div className="flex justify-start">
          <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-tl-sm text-sm leading-relaxed bg-amber-50 text-stone-800 border border-amber-200">
            {streamingAiText}
            <span className="inline-block w-1 h-3.5 bg-amber-400 ml-0.5 animate-pulse" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* Partial transcript (voice mode in-progress) */}
      {partialTranscript && (
        <div className="flex justify-end">
          <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-tr-sm text-sm leading-relaxed bg-stone-100 text-stone-500 italic border border-stone-200">
            {partialTranscript}
            <span className="inline-block w-1 h-3.5 bg-stone-400 ml-0.5 animate-pulse" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
}
